import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import StorefrontLayout from "../shared/StorefrontLayout";
import { getLang, t } from "../../i18n";

const STAFF_ROLES = ["developer", "super-admin", "admin"];
const ADMIN_AUTH_UPDATED_EVENT = "ft-admin-auth-updated";
const USER_AUTH_UPDATED_EVENT = "ft-user-auth-updated";
const GOOGLE_GSI_SCRIPT_ID = "google-identity-services-sdk";

function loadGoogleIdentityScript() {
  if (typeof document === "undefined") {
    return Promise.resolve(false);
  }

  if (window.google?.accounts?.id) {
    return Promise.resolve(true);
  }

  const existing = document.getElementById(GOOGLE_GSI_SCRIPT_ID);
  if (existing) {
    const existingStatus = String(existing.getAttribute("data-gsi-status") || "").trim();
    if (existingStatus === "loaded") {
      return Promise.resolve(Boolean(window.google?.accounts?.id));
    }
    if (existingStatus === "failed") {
      return Promise.resolve(false);
    }

    return new Promise((resolve) => {
      const timeoutId = window.setTimeout(() => resolve(false), 6000);
      existing.addEventListener("load", () => resolve(Boolean(window.google?.accounts?.id)), { once: true });
      existing.addEventListener("error", () => resolve(false), { once: true });
      existing.addEventListener("load", () => clearTimeout(timeoutId), { once: true });
      existing.addEventListener("error", () => clearTimeout(timeoutId), { once: true });
    });
  }

  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.id = GOOGLE_GSI_SCRIPT_ID;
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    const timeoutId = window.setTimeout(() => {
      script.setAttribute("data-gsi-status", "failed");
      resolve(false);
    }, 6000);
    script.onload = () => {
      clearTimeout(timeoutId);
      script.setAttribute("data-gsi-status", "loaded");
      resolve(Boolean(window.google?.accounts?.id));
    };
    script.onerror = () => {
      clearTimeout(timeoutId);
      script.setAttribute("data-gsi-status", "failed");
      resolve(false);
    };
    document.head.appendChild(script);
  });
}

function i18nText(key, fallback) {
  const resolved = t(key);
  return typeof resolved === "string" && resolved !== key ? resolved : fallback;
}

function normalizeRoleValue(role) {
  const normalized = String(role || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");

  if (["developer", "dev", "root", "owner", "system"].includes(normalized)) {
    return "developer";
  }

  if (normalized === "superadmin") {
    return "super-admin";
  }

  if (["super-admin", "super administrator", "sa"].includes(normalized)) {
    return "super-admin";
  }

  if (["admin", "administrator", "manager", "staff", "operator", "ops"].includes(normalized)) {
    return "admin";
  }

  if (normalized === "user") {
    return "customer";
  }

  if (["customer", "member", "buyer", "client", "shopper"].includes(normalized)) {
    return "customer";
  }

  if (normalized === "guest") {
    return "guest";
  }

  if (normalized === "administrator") {
    return "admin";
  }

  const numericRole = Number(role);
  if (Number.isFinite(numericRole)) {
    if (numericRole >= 3) {
      return "developer";
    }
    if (numericRole === 2) {
      return "super-admin";
    }
    if (numericRole === 1) {
      return "admin";
    }
    if (numericRole === 0) {
      return "customer";
    }
  }

  return normalized;
}

function resolveAdminFlag(candidate) {
  if (!candidate || typeof candidate !== "object") {
    return "";
  }

  if (candidate.isAdmin === true || candidate.IsAdmin === true) {
    return "admin";
  }

  return "";
}

function resolveRole(profileData, loginData) {
  const candidates = [
    loginData?.user?.role,
    loginData?.user?.roleName,
    loginData?.user?.userType,
    loginData?.user?.accountType,
    Array.isArray(loginData?.user?.roles) ? loginData.user.roles[0] : "",
    loginData?.admin?.role,
    loginData?.admin?.roleName,
    loginData?.admin?.userType,
    loginData?.admin?.accountType,
    Array.isArray(loginData?.admin?.roles) ? loginData.admin.roles[0] : "",
    loginData?.role,
    loginData?.roleName,
    loginData?.userType,
    loginData?.accountType,
    Array.isArray(loginData?.roles) ? loginData.roles[0] : "",
    profileData?.user?.role,
    profileData?.user?.roleName,
    profileData?.user?.userType,
    profileData?.user?.accountType,
    Array.isArray(profileData?.user?.roles) ? profileData.user.roles[0] : "",
    profileData?.role,
    profileData?.roleName,
    profileData?.userType,
    profileData?.accountType,
    Array.isArray(profileData?.roles) ? profileData.roles[0] : ""
  ];

  for (const candidate of candidates) {
    const normalized = normalizeRoleValue(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return (
    resolveAdminFlag(loginData?.user) ||
    resolveAdminFlag(loginData?.admin) ||
    resolveAdminFlag(loginData) ||
    resolveAdminFlag(profileData?.user) ||
    resolveAdminFlag(profileData) ||
    "customer"
  );
}

const COMMON_EMAIL_DOMAINS = new Set([
  "gmail.com",
  "outlook.com",
  "hotmail.com",
  "icloud.com",
  "yahoo.com",
  "qq.com",
  "163.com",
  "126.com",
  "foxmail.com"
]);

const EMAIL_DOMAIN_CORRECTIONS = {
  "gamil.com": "gmail.com",
  "gmial.com": "gmail.com",
  "gmail.con": "gmail.com",
  "hotmial.com": "hotmail.com",
  "outllok.com": "outlook.com",
  "outlok.com": "outlook.com",
  "yaho.com": "yahoo.com",
  "icloud.con": "icloud.com",
  "qq.con": "qq.com",
  "163.con": "163.com",
  "126.con": "126.com",
  "foxmai.com": "foxmail.com"
};

function countCharacters(text) {
  return Array.from(String(text || "")).length;
}

function containsCjk(text) {
  return /[\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff]/.test(String(text || ""));
}

function translateAuthText(text) {
  const value = String(text || "").trim();
  if (!value) {
    return "";
  }

  if (value === "ACCOUNT_NOT_FOUND") {
    return t("authLogin.accountNotFound");
  }

  if (value === "PASSWORD_INCORRECT") {
    return t("authLogin.passwordIncorrect");
  }

  if (value === "LOGIN_REGISTRATION_NOT_OPEN" || value === "当前不开放登录和注册") {
    return t("authLogin.registrationNotOpen");
  }

  if (
    value === "ACCOUNT_RESTRICTED" ||
    value === "Restricted account" ||
    /This account has been restricted from logging in\./i.test(value)
  ) {
    return t("authLogin.accountRestricted");
  }

  const exactKeyMap = {
    "请输入用户名，至少 3 个字符。": "authLogin.usernameRequired",
    "Enter a username with at least 3 characters.": "authLogin.usernameRequired",
    "用户名不能少于 3 个字符。": "authLogin.usernameMin",
    "Username must be at least 3 characters.": "authLogin.usernameMin",
    "用户名不能超过 24 个字符。": "authLogin.usernameMax",
    "Username must be 24 characters or fewer.": "authLogin.usernameMax",
    "用户名只能包含中英文、数字、下划线和短横线。": "authLogin.usernamePattern",
    "Username may only contain letters, numbers, underscores, and hyphens.": "authLogin.usernamePattern",
    "该用户名为系统保留字，请更换。": "authLogin.usernameReserved",
    "This username is reserved. Please choose another one.": "authLogin.usernameReserved",
    "用户名格式可用。": "authLogin.usernameFormatOk",
    "Username format looks good.": "authLogin.usernameFormatOk",
    "请输入邮箱地址。": "authLogin.emailRequired",
    "Enter an email address.": "authLogin.emailRequired",
    "请输入合法邮箱地址。": "authLogin.emailInvalid",
    "Enter a valid email address.": "authLogin.emailInvalid",
    "邮箱格式可用。": "authLogin.emailFormatOk",
    "Email format looks good.": "authLogin.emailFormatOk",
    "建议优先使用常见邮箱，例如 Gmail、Outlook、QQ、163、126、Foxmail。": "authLogin.emailProviderHint",
    "Use a common email provider when possible, such as Gmail, Outlook, QQ Mail, 163, 126, or Foxmail.": "authLogin.emailProviderHint",
    "请输入密码。": "authLogin.passwordRequired",
    "Password is required": "authLogin.passwordRequired",
    "密码长度不能少于 6 位。": "authLogin.passwordMin",
    "Password must be at least 6 characters.": "authLogin.passwordMin",
    "Password must be at least 6 characters": "authLogin.passwordMin",
    "请输入确认密码。": "authLogin.confirmPasswordRequired",
    "Enter the confirmation password.": "authLogin.confirmPasswordRequired",
    "两次输入的密码不一致。": "authLogin.passwordMismatch",
    "The two passwords do not match.": "authLogin.passwordMismatch",
    "Passwords do not match": "authLogin.passwordMismatch",
    "两次密码输入一致。": "authLogin.passwordMatch",
    "Passwords match.": "authLogin.passwordMatch",
    "请输入邮箱验证码。": "authLogin.emailCodeRequired",
    "Enter the email verification code.": "authLogin.emailCodeRequired",
    "请输入图形验证码。": "authLogin.captchaRequired",
    "Enter the captcha code.": "authLogin.captchaRequired",
    "Captcha is required": "authLogin.captchaRequired",
    "Please input captcha code": "authLogin.captchaRequired",
    "请先输入图形验证码，再发送邮箱验证码。": "authLogin.enterCaptchaBeforeEmailCode",
    "Enter the captcha code before requesting the email verification code.": "authLogin.enterCaptchaBeforeEmailCode",
    "Captcha expired, please refresh": "authLogin.captchaExpired",
    "The captcha has expired. Please refresh it and try again.": "authLogin.captchaExpired",
    "Captcha is incorrect": "authLogin.captchaIncorrect",
    "The captcha code is incorrect. Please try again.": "authLogin.captchaIncorrect",
    "邮箱验证码已发送。": "authLogin.emailCodeSent",
    "The email verification code has been sent.": "authLogin.emailCodeSent",
    "请直接输入这 6 位验证码继续注册。": "authLogin.previewCodeContinueRegistration",
    "Enter this 6-digit code to continue registration.": "authLogin.previewCodeContinueRegistration",
    "立即登录": "authLogin.loginNow",
    "Log In Now": "authLogin.loginNow",
    "请先完成图形验证码，再发送邮箱验证码。发送成功后系统会自动刷新一张新的图形验证码，供最终注册提交使用。": "authLogin.registerCaptchaHint",
    "Complete the captcha first, then request the email verification code. After the code is sent, a new captcha will be loaded automatically for the final registration step.": "authLogin.registerCaptchaHint",
    "注册时需同时通过图形验证码和邮箱验证码校验。": "authLogin.registerRequirementHint",
    "Registration requires both captcha verification and email-code verification.": "authLogin.registerRequirementHint",
    "看不清验证码可以点击右侧图片刷新。": "authLogin.captchaRefreshHint",
    "If the captcha is hard to read, click the image on the right to refresh it.": "authLogin.captchaRefreshHint",
    "已有账号？立即登录": "authLogin.switchToLogin",
    "Already have an account? Log in now": "authLogin.switchToLogin",
    "还没有登录？马上注册": "authLogin.switchToRegister",
    "No account yet? Register now": "authLogin.switchToRegister",
    "发送中...": "authLogin.sending",
    "Sending...": "authLogin.sending",
    "发送邮箱验证码": "authLogin.sendEmailCode",
    "Send Email Code": "authLogin.sendEmailCode",
    "发送过于频繁，请稍后再试。": "authLogin.tooManyRequests",
    "Too many requests. Please try again later.": "authLogin.tooManyRequests",
    "邮箱验证码已发送，请查收邮件。": "authLogin.emailCodeSentInbox",
    "The email verification code has been sent. Please check your inbox.": "authLogin.emailCodeSentInbox",
    "邮箱验证码已生成，当前环境使用预览码。": "authLogin.previewCodeGenerated",
    "The email verification code has been generated. This environment is using a preview code.": "authLogin.previewCodeGenerated",
    "邮件发送失败，请检查 SMTP 配置后重试。": "authLogin.smtpConfigRetry",
    "Failed to send the email. Check the SMTP configuration and try again.": "authLogin.smtpConfigRetry",
    "邮箱发送服务未配置，请联系管理员。": "authLogin.emailServiceNotConfigured",
    "The email delivery service is not configured. Please contact the administrator.": "authLogin.emailServiceNotConfigured",
    "Email already exists": "authLogin.emailAlreadyRegistered",
    "Email already exists.": "authLogin.emailAlreadyRegistered",
    "Username already exists": "authLogin.usernameAlreadyRegistered",
    "Username already exists.": "authLogin.usernameAlreadyRegistered",
    "Google authentication failed": "authLogin.googleAuthFailed",
    "Google login failed": "authLogin.googleAuthFailed",
    "Google register failed": "authLogin.googleAuthFailed",
    "Google credential verification timed out": "authLogin.googleAuthTimeout",
    "Google credential verification timed out. Check server outbound access to Google APIs or increase GOOGLE_OAUTH_VERIFY_TIMEOUT_MS": "authLogin.googleAuthTimeout",
    "Failed to verify Google credential due to network error. Check server outbound access to Google APIs": "authLogin.googleAuthNetworkError",
    "Request failed. Please try again or contact support.": "authLogin.requestFailed"
  };

  if (exactKeyMap[value]) {
    return t(exactKeyMap[value]);
  }

  const suggestionMatch = value.match(/^(?:是否想使用|Did you mean)\s+(.+)\s*\?$/i);
  if (suggestionMatch) {
    return `${t("authLogin.didYouMean")} ${suggestionMatch[1]}?`;
  }

  const countdownMatch = value.match(/^(?:(\d+)s后重发|Resend in\s+(\d+)s)$/i);
  if (countdownMatch) {
    const seconds = countdownMatch[1] || countdownMatch[2];
    return `${t("authLogin.resendIn")} ${seconds}s`;
  }

  if (value.startsWith("当前环境预览码：") || /^Preview code for the current environment:/i.test(value)) {
    const code = value.includes(":") ? value.slice(value.indexOf(":") + 1).trim() : value.slice("当前环境预览码：".length).trim();
    return code ? `${t("authLogin.previewCodeCurrentEnv")}: ${code}` : t("authLogin.previewCodeCurrentEnv");
  }

  if (value.startsWith("开发环境预览码：") || /^Preview code for development:/i.test(value)) {
    const code = value.includes(":") ? value.slice(value.indexOf(":") + 1).trim() : value.slice("开发环境预览码：".length).trim();
    return code ? `${t("authLogin.previewCodeDevelopment")}: ${code}` : t("authLogin.previewCodeDevelopment");
  }

  if (containsCjk(value)) {
    return getLang() === "zh" ? value : t("authLogin.requestFailed");
  }

  return getLang() === "zh" ? t("authLogin.requestFailed") : value;
}

function validateUsernameInput(username) {
  const rawValue = String(username || "").trim();
  if (!rawValue) {
    return { ok: false, message: i18nText("authLogin.usernameRequired", "Enter a username with at least 3 characters.") };
  }

  if (countCharacters(rawValue) < 3) {
    return { ok: false, message: i18nText("authLogin.usernameMin", "Username must be at least 3 characters.") };
  }

  if (countCharacters(rawValue) > 24) {
    return { ok: false, message: i18nText("authLogin.usernameMax", "Username must be 24 characters or fewer.") };
  }

  if (!/^[\p{L}\p{N}_-]+$/u.test(rawValue)) {
    return { ok: false, message: i18nText("authLogin.usernamePattern", "Username may only contain letters, numbers, underscores, and hyphens.") };
  }

  if (["admin", "administrator", "root", "system", "support", "service", "guest", "developer", "superadmin", "super-admin", "api"].includes(rawValue.toLowerCase().replace(/[\s_.-]+/g, ""))) {
    return { ok: false, message: i18nText("authLogin.usernameReserved", "This username is reserved. Please choose another one.") };
  }

  return { ok: true, message: i18nText("authLogin.usernameFormatOk", "Username format looks good.") };
}

function validateEmailInput(email) {
  const normalized = String(email || "").trim().toLowerCase();
  if (!normalized) {
    return { ok: false, message: i18nText("authLogin.emailRequired", "Enter an email address."), hint: "" };
  }

  const parts = normalized.split("@");
  const hint = parts.length === 2
    ? EMAIL_DOMAIN_CORRECTIONS[parts[1]]
      ? `${i18nText("authLogin.didYouMean", "Did you mean")} ${parts[0]}@${EMAIL_DOMAIN_CORRECTIONS[parts[1]]}?`
      : !COMMON_EMAIL_DOMAINS.has(parts[1])
        ? i18nText("authLogin.emailProviderHint", "Use a common email provider when possible, such as Gmail, Outlook, QQ Mail, 163, 126, or Foxmail.")
        : ""
    : "";

  if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(normalized)) {
    return { ok: false, message: i18nText("authLogin.emailInvalid", "Enter a valid email address."), hint };
  }

  return { ok: true, message: i18nText("authLogin.emailFormatOk", "Email format looks good."), hint };
}

function getRegisterFormError({ username, email, password, confirmPassword, emailCode, usernameExists, emailExists }) {
  const usernameResult = validateUsernameInput(username);
  if (!usernameResult.ok) {
    return usernameResult.message;
  }

  const emailResult = validateEmailInput(email);
  if (!emailResult.ok) {
    return emailResult.message;
  }

  if (usernameExists) {
    return i18nText("authLogin.usernameExists", "Username already exists. Please choose another username or log in directly.");
  }

  if (emailExists) {
    return i18nText("authLogin.emailExists", "Email already exists. Please use another email or go to login.");
  }

  if (password.length < 6) {
    return i18nText("authLogin.passwordMin", "Password must be at least 6 characters.");
  }

  if (!confirmPassword) {
    return i18nText("authLogin.confirmPasswordRequired", "Enter the confirmation password.");
  }

  if (password !== confirmPassword) {
    return i18nText("authLogin.passwordMismatch", "The two passwords do not match.");
  }

  if (!String(emailCode || "").trim()) {
    return i18nText("authLogin.emailCodeRequired", "Enter the email verification code.");
  }

  return "";
}

function getResetPasswordFormError({ email, password, confirmPassword, emailCode, captchaId, captchaCode }) {
  const emailResult = validateEmailInput(email);
  if (!emailResult.ok) {
    return emailResult.message;
  }

  if (password.length < 6) {
    return i18nText("authLogin.newPasswordMin", "New password must be at least 6 characters.");
  }

  if (!confirmPassword) {
    return i18nText("authLogin.confirmPasswordRequired", "Enter the confirmation password.");
  }

  if (password !== confirmPassword) {
    return i18nText("authLogin.passwordMismatch", "The two passwords do not match.");
  }

  if (!String(emailCode || "").trim()) {
    return i18nText("authLogin.resetCodeRequired", "Enter the reset code sent to your email.");
  }

  if (!captchaId || !String(captchaCode || "").trim()) {
    return i18nText("authLogin.captchaRequired", "Enter the captcha code.");
  }

  return "";
}

function resolveAuthViewFromSearch(search = "") {
  const searchParams = new URLSearchParams(search || "");
  const candidate = String(searchParams.get("auth") || searchParams.get("view") || "")
    .trim()
    .toLowerCase();

  if (["login", "register", "forgot"].includes(candidate)) {
    return candidate;
  }

  return "login";
}

function normalizeAuthView(value) {
  const candidate = String(value || "")
    .trim()
    .toLowerCase();

  if (["login", "register", "forgot"].includes(candidate)) {
    return candidate;
  }

  return "login";
}

export default function LoginPage({ apiBaseUrl, storefrontLocale = "en", allowRegistration = true }) {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTimerRef = useRef(null);
  const authShellRef = useRef(null);
  const authSuccessPanelRef = useRef(null);
  const googleButtonHostRef = useRef(null);
  const googleAuthInFlightRef = useRef(false);
  const [redirectTarget] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search || "");
    const nextFromQuery = String(searchParams.get("next") || "").trim();
    const nextFromCache = String(localStorage.getItem("ft_post_login_redirect") || "").trim();
    const target = nextFromQuery || nextFromCache || "";
    return target.startsWith("/") ? target : "";
  });
  const [currency, setCurrency] = useState("USD");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [captchaId, setCaptchaId] = useState("");
  const [captchaImage, setCaptchaImage] = useState("");
  const [captchaCode, setCaptchaCode] = useState("");
  const [disableFrontendLoginCaptcha, setDisableFrontendLoginCaptcha] = useState(false);
  const [verifiedCaptchaIdForRegister, setVerifiedCaptchaIdForRegister] = useState("");
  const [verifiedCaptchaCodeForRegister, setVerifiedCaptchaCodeForRegister] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailCodeCountdown, setEmailCodeCountdown] = useState(0);
  const [sendingEmailCode, setSendingEmailCode] = useState(false);
  const [googleAuthEnabled, setGoogleAuthEnabled] = useState(false);
  const [googleAuthClientId, setGoogleAuthClientId] = useState("");
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleSdkRequested, setGoogleSdkRequested] = useState(false);
  const [googleSdkLoadFailed, setGoogleSdkLoadFailed] = useState(false);
  const [pendingGoogleRegistration, setPendingGoogleRegistration] = useState(null);
  const [usernameAvailability, setUsernameAvailability] = useState({
    checking: false,
    exists: false,
    message: ""
  });
  const [emailAvailability, setEmailAvailability] = useState({
    checking: false,
    exists: false,
    message: ""
  });
  const [authView, setAuthView] = useState(() => resolveAuthViewFromSearch(window.location.search || ""));
  const [authSuccessNotice, setAuthSuccessNotice] = useState("");
  const [authRedirectMs, setAuthRedirectMs] = useState(0);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const registrationDisabledMessage = storefrontLocale === "zh"
    ? "用户注册功能已关闭。"
    : "User registration is currently disabled.";

  const registerMode = authView === "register";
  const forgotPasswordMode = authView === "forgot";
  const googleRegistrationMode = Boolean(pendingGoogleRegistration?.credential);
  const recoveryMode = registerMode || forgotPasswordMode || googleRegistrationMode;
  const hasStoredAuthSession = typeof window !== "undefined" && Boolean(
    localStorage.getItem("ft_admin_token") || localStorage.getItem("ft_user_token")
  );
  const shouldShowGoogleAuth = !forgotPasswordMode && !googleRegistrationMode && !hasStoredAuthSession && googleAuthEnabled;
  const shouldLoadGoogleAuth = shouldShowGoogleAuth && googleSdkRequested;
  const authTitle = googleRegistrationMode
    ? i18nText("authLogin.googleCreateAccountTitle", "Complete Your Account")
    : registerMode
    ? authSuccessNotice
      ? t("authLogin.registrationComplete")
      : t("authLogin.createAccount")
    : forgotPasswordMode
      ? authSuccessNotice
        ? t("authLogin.passwordUpdated")
        : t("authLogin.recoverPassword")
      : t("authLogin.accountLogin");
  const authDescription = googleRegistrationMode
    ? i18nText(
      "authLogin.googleCreateAccountDesc",
      "Google verified your email. Set a password to create a customer account and finish sign-in."
    )
    : registerMode
    ? authSuccessNotice
      ? t("authLogin.registerSuccessDesc")
      : t("authLogin.registerDesc")
    : forgotPasswordMode
      ? authSuccessNotice
        ? t("authLogin.resetSuccessDesc")
        : t("authLogin.forgotDesc")
      : t("authLogin.loginDesc");

  const usernameValidation = validateUsernameInput(username);
  const emailValidation = validateEmailInput(email);
  const confirmPasswordState = !recoveryMode || !confirmPassword
    ? ""
    : password === confirmPassword
      ? t("authLogin.passwordMatch")
      : t("authLogin.passwordMismatch");
  const registerPrerequisitesMet =
    registerMode &&
    usernameValidation.ok &&
    emailValidation.ok &&
    !usernameAvailability.checking &&
    !emailAvailability.checking &&
    !usernameAvailability.exists &&
    !emailAvailability.exists &&
    password.length >= 6 &&
    Boolean(confirmPassword) &&
    password === confirmPassword;
  const registerCaptchaStepVisible = !registerMode || registerPrerequisitesMet;
  const canSendEmailCode =
    !sendingEmailCode &&
    emailCodeCountdown <= 0 &&
    emailValidation.ok &&
    Boolean(captchaId) &&
    Boolean(String(captchaCode || "").trim()) &&
    (!registerMode || registerPrerequisitesMet);
  const shouldShowLoginCaptcha = recoveryMode || !disableFrontendLoginCaptcha;

  function syncAuthViewInUrl(nextView, options = {}) {
    const normalizedView = normalizeAuthView(nextView);
    const nextParams = new URLSearchParams(location.search || "");
    nextParams.delete("view");

    if (normalizedView === "login") {
      nextParams.delete("auth");
    } else {
      nextParams.set("auth", normalizedView);
    }

    const currentSearch = String(location.search || "").replace(/^\?/, "");
    const nextSearch = nextParams.toString();
    if (currentSearch !== nextSearch) {
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : ""
        },
        { replace: options.replace !== false }
      );
    }

    return normalizedView;
  }

  useEffect(() => {
    if (allowRegistration) {
      return;
    }

    if (authView === "register") {
      const normalizedView = syncAuthViewInUrl("login");
      setAuthView(normalizedView);
      setError(registrationDisabledMessage);
      setNotice("");
    }
  }, [allowRegistration, authView, location.pathname, location.search, navigate, registrationDisabledMessage]);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        window.clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  function runDelayedAction(action, delayMs = 1500) {
    if (redirectTimerRef.current) {
      window.clearTimeout(redirectTimerRef.current);
    }

    redirectTimerRef.current = window.setTimeout(() => {
      redirectTimerRef.current = null;
      action();
    }, delayMs);
  }

  useEffect(() => {
    loadCaptcha();
  }, [authView]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/storefront/config`, { cache: "no-store" });
        const data = await response.json();
        if (cancelled) {
          return;
        }
        setDisableFrontendLoginCaptcha(Boolean(data?.auth?.disableFrontendLoginCaptcha));
      } catch (_err) {
        if (!cancelled) {
          setDisableFrontendLoginCaptcha(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const targets = [
        `${apiBaseUrl}/auth/google/config`,
        "/auth/google/config"
      ];

      try {
        let loaded = false;
        for (const target of targets) {
          const response = await fetch(target, { cache: "no-store" });
          if (response.status === 404) {
            continue;
          }
          const data = await response.json();
          if (!response.ok) {
            throw new Error(String(data?.message || "Failed to load Google auth config"));
          }

          if (cancelled) {
            return;
          }

          setGoogleAuthEnabled(Boolean(data?.enabled));
          setGoogleAuthClientId(String(data?.clientId || "").trim());
          loaded = true;
          break;
        }

        if (!loaded && !cancelled) {
          setGoogleAuthEnabled(false);
          setGoogleAuthClientId("");
        }
      } catch (_err) {
        if (!cancelled) {
          setGoogleAuthEnabled(false);
          setGoogleAuthClientId("");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl]);

  useEffect(() => {
    const viewFromUrl = resolveAuthViewFromSearch(location.search || "");
    setAuthView((current) => (current === viewFromUrl ? current : viewFromUrl));
  }, [location.search]);

  useEffect(() => {
    if (shouldShowGoogleAuth) {
      return;
    }

    setGoogleSdkRequested(false);
    setGoogleSdkLoadFailed(false);
  }, [shouldShowGoogleAuth]);

  function clearAuthFormInputs() {
    setEmail("");
    setUsername("");
    setName("");
    setPassword("");
    setConfirmPassword("");
    setCaptchaCode("");
    setEmailCode("");
    setEmailCodeCountdown(0);
    setVerifiedCaptchaIdForRegister("");
    setVerifiedCaptchaCodeForRegister("");
    setUsernameAvailability({ checking: false, exists: false, message: "" });
    setEmailAvailability({ checking: false, exists: false, message: "" });
    setPendingGoogleRegistration(null);
  }

  async function fetchAvailability(params = {}) {
    const query = new URLSearchParams();
    const usernameValue = String(params.username || "").trim();
    const emailValue = String(params.email || "").trim();

    if (usernameValue) {
      query.set("username", usernameValue);
    }
    if (emailValue) {
      query.set("email", emailValue);
    }

    const targets = [
      `${apiBaseUrl}/auth/availability?${query.toString()}`,
      `/auth/availability?${query.toString()}`
    ];

    let lastError = null;
    for (const target of targets) {
      try {
        const response = await fetch(target, { cache: "no-store" });
        const data = await response.json();

        if (response.status === 404) {
          continue;
        }

        if (!response.ok) {
          throw new Error(data.message || "Failed to check availability");
        }

        return data;
      } catch (err) {
        lastError = err;
      }
    }

    throw lastError || new Error("Failed to check availability");
  }

  useEffect(() => {
    if (!registerMode) {
      return;
    }

    const value = String(username || "").trim();
    if (!value || !usernameValidation.ok) {
      setUsernameAvailability({ checking: false, exists: false, message: "" });
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setUsernameAvailability({ checking: true, exists: false, message: t("authLogin.checkingUsername") });
      try {
        const data = await fetchAvailability({ username: value });

        if (cancelled) {
          return;
        }

        if (Boolean(data.usernameExists)) {
          setUsernameAvailability({ checking: false, exists: true, message: t("authLogin.usernameAlreadyRegistered") });
          return;
        }

        setUsernameAvailability({ checking: false, exists: false, message: t("authLogin.usernameAvailable") });
      } catch (_err) {
        if (!cancelled) {
          setUsernameAvailability({ checking: false, exists: false, message: t("authLogin.usernameVerifyFailed") });
        }
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [registerMode, username, usernameValidation.ok, apiBaseUrl]);

  useEffect(() => {
    if (!registerMode) {
      return;
    }

    const value = String(email || "").trim();
    if (!value || !emailValidation.ok) {
      setEmailAvailability({ checking: false, exists: false, message: "" });
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setEmailAvailability({ checking: true, exists: false, message: t("authLogin.checkingEmail") });
      try {
        const data = await fetchAvailability({ email: value });

        if (cancelled) {
          return;
        }

        if (Boolean(data.emailExists)) {
          setEmailAvailability({ checking: false, exists: true, message: t("authLogin.emailAlreadyRegistered") });
          return;
        }

        setEmailAvailability({ checking: false, exists: false, message: t("authLogin.emailAvailable") });
      } catch (_err) {
        if (!cancelled) {
          setEmailAvailability({ checking: false, exists: false, message: t("authLogin.emailVerifyFailed") });
        }
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [registerMode, email, emailValidation.ok, apiBaseUrl]);

  function scrollAuthShellIntoView(block = "start") {
    window.requestAnimationFrame(() => {
      if (authShellRef.current && typeof authShellRef.current.scrollIntoView === "function") {
        authShellRef.current.scrollIntoView({ behavior: "auto", block });
        return;
      }

      window.scrollTo({ top: 0, behavior: "auto" });
    });
  }

  function resetAuthSuccessState() {
    setAuthSuccessNotice("");
    setAuthRedirectMs(0);
  }

  function switchToLoginMode(options = {}) {
    if (redirectTimerRef.current) {
      window.clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }

    const normalizedView = syncAuthViewInUrl("login");
    setAuthView(normalizedView);
    clearAuthFormInputs();
    const identifier = String(options.identifier || "").trim();
    if (identifier) {
      setEmail(identifier);
    }
    resetAuthSuccessState();
    setNotice("");
    scrollAuthShellIntoView("start");
  }

  function resetRecoveryState() {
    setCaptchaCode("");
    setVerifiedCaptchaIdForRegister("");
    setVerifiedCaptchaCodeForRegister("");
    setEmailCode("");
    setEmailCodeCountdown(0);
    setConfirmPassword("");
    setPassword("");
    resetAuthSuccessState();
  }

  function switchAuthView(nextView) {
    const normalizedView = syncAuthViewInUrl(nextView);
    setAuthView(normalizedView);
    setError("");
    setNotice("");
    clearAuthFormInputs();
    resetRecoveryState();
    scrollAuthShellIntoView("start");
  }

  useEffect(() => {
    if (authView !== "login") {
      return;
    }

    const timer = window.setTimeout(() => {
      scrollAuthShellIntoView("start");
    }, 40);

    return () => {
      window.clearTimeout(timer);
    };
  }, [authView]);

  useEffect(() => {
    if (!recoveryMode || !authSuccessNotice) {
      return;
    }

    const timer = window.setTimeout(() => {
      if (authSuccessPanelRef.current && typeof authSuccessPanelRef.current.scrollIntoView === "function") {
        authSuccessPanelRef.current.scrollIntoView({ behavior: "auto", block: "center" });
      }
    }, 40);

    return () => {
      window.clearTimeout(timer);
    };
  }, [recoveryMode, authSuccessNotice]);

  async function loadCaptcha() {
    try {
      const response = await fetch(`${apiBaseUrl}/auth/captcha`, { cache: "no-store" });
      const data = await response.json();
      if (response.ok) {
        setCaptchaId(String(data.captchaId || ""));
        setCaptchaImage(String(data.image || ""));
        setCaptchaCode("");
      }
    } catch (_err) {
      setCaptchaImage("");
      setCaptchaId("");
    }
  }

  async function parseJsonResponse(response) {
    const text = await response.text();
    if (!text) {
      return {};
    }

    try {
      return JSON.parse(text);
    } catch (_err) {
      return {};
    }
  }

  const completeAuthenticatedSession = useCallback(async (data) => {
    const token = data.token || "";
    if (!token) {
      throw new Error(t("authLogin.loginTokenMissing"));
    }

    let profileData = {};
    try {
      const profileResponse = await fetch(`${apiBaseUrl}/auth/me?ts=${Date.now()}`, {
        cache: "no-store",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const nextProfileData = await parseJsonResponse(profileResponse);

      if (profileResponse.ok) {
        profileData = nextProfileData;
      } else if (String(nextProfileData?.code || "").trim() === "ACCOUNT_RESTRICTED") {
        throw new Error(nextProfileData.message || t("authLogin.profileLoadFailed"));
      }
    } catch (err) {
      if (String(err?.message || "").trim()) {
        throw err;
      }
    }

    const loginRole = resolveRole(null, data);
    const role = STAFF_ROLES.includes(loginRole) ? loginRole : resolveRole(profileData, data);
    const resolvedUserProfile = profileData?.user && typeof profileData.user === "object"
      ? profileData.user
      : (data?.user && typeof data.user === "object" ? data.user : {});

    const isStaffRole = STAFF_ROLES.includes(role);
    if (isStaffRole) {
      localStorage.setItem("ft_admin_token", token);
      localStorage.setItem("ft_auth_provider", String(data?.provider || "password") || "password");
      localStorage.setItem("ft_admin_role", role || "customer");
      localStorage.setItem("ft_admin_expires_at", data.expiresAt || "");
      window.dispatchEvent(new CustomEvent(ADMIN_AUTH_UPDATED_EVENT));
      window.dispatchEvent(new CustomEvent(USER_AUTH_UPDATED_EVENT));

      localStorage.removeItem("ft_post_login_redirect");
      setNotice(t("authLogin.loginSuccessAdmin"));
      runDelayedAction(() => {
        window.location.assign(redirectTarget || "/admin");
      });
      return;
    }

    localStorage.removeItem("ft_admin_token");
    localStorage.removeItem("ft_admin_role");
    localStorage.removeItem("ft_admin_expires_at");
    localStorage.removeItem("ft_post_login_redirect");
    window.dispatchEvent(new CustomEvent(ADMIN_AUTH_UPDATED_EVENT));

    localStorage.setItem("ft_user_token", token);
    localStorage.setItem("ft_auth_provider", String(data?.provider || "password") || "password");
    window.dispatchEvent(new CustomEvent(USER_AUTH_UPDATED_EVENT));
    localStorage.setItem(
      "ft_user_profile",
      JSON.stringify({
        id: resolvedUserProfile.id,
        name: resolvedUserProfile.name,
        email: resolvedUserProfile.email,
        role
      })
    );

    setNotice(
      data?.provider === "google" && data?.isNewUser
        ? t("authLogin.googleRegisterSuccess")
        : t("authLogin.loginSuccessStorefront")
    );
    runDelayedAction(() => {
      navigate(`/products?lang=${encodeURIComponent(storefrontLocale)}&currency=${encodeURIComponent(currency)}`);
    });
  }, [apiBaseUrl, currency, navigate, redirectTarget, storefrontLocale]);

  const startGoogleRegistration = useCallback((credential, profile = {}) => {
    const normalizedCredential = String(credential || "").trim();
    const normalizedEmail = String(profile?.email || "").trim().toLowerCase();
    if (!normalizedCredential || !normalizedEmail) {
      return;
    }

    setPendingGoogleRegistration({
      credential: normalizedCredential,
      email: normalizedEmail,
      name: String(profile?.name || "").trim()
    });
    setAuthView(syncAuthViewInUrl("login"));
    setEmail(normalizedEmail);
    setUsername("");
    setName(String(profile?.name || "").trim());
    setPassword("");
    setConfirmPassword("");
    setCaptchaCode("");
    setEmailCode("");
    setNotice(i18nText("authLogin.googleCreateAccountHint", "No account exists for this Google email yet. Set a password to create your customer account."));
    setError("");
    scrollAuthShellIntoView("start");
  }, [location.pathname, location.search, navigate]);

  const submitGoogleCredential = useCallback(async (credential, mode) => {
    const normalizedCredential = String(credential || "").trim();
    if (!normalizedCredential || googleAuthInFlightRef.current) {
      return;
    }

    googleAuthInFlightRef.current = true;
    setError("");
    setNotice("");
    setGoogleLoading(true);
    setLoading(true);

    try {
      const endpoint = mode === "register" ? "google/register" : "google/login";
      const response = await fetch(`${apiBaseUrl}/auth/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credential: normalizedCredential })
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        const authError = new Error(translateAuthText(data.message || t("authLogin.googleAuthFailed")));
        authError.code = String(data?.code || "").trim();
        authError.details = data;
        throw authError;
      }

      setPendingGoogleRegistration(null);
      await completeAuthenticatedSession(data);
    } catch (err) {
      const message = String(err?.message || "").trim();
      const code = String(err?.code || "").trim();
      const details = err?.details && typeof err.details === "object" ? err.details : {};
      if (code === "ACCOUNT_NOT_FOUND" && details?.googleProfile?.email) {
        startGoogleRegistration(normalizedCredential, details.googleProfile);
        return;
      }
      setError(translateAuthText(code || message || t("authLogin.googleAuthFailed")));
    } finally {
      googleAuthInFlightRef.current = false;
      setGoogleLoading(false);
      setLoading(false);
      loadCaptcha();
    }
  }, [apiBaseUrl, completeAuthenticatedSession, startGoogleRegistration]);

  function handleGoogleFallbackClick() {
    setNotice("");
    setError(t("authLogin.googleAuthOffline"));
  }

  function requestGoogleAuthWidget() {
    setNotice("");
    setError("");
    setGoogleSdkLoadFailed(false);
    setGoogleSdkRequested(true);
  }

  useEffect(() => {
    if (!shouldLoadGoogleAuth || !googleAuthClientId) {
      setGoogleSdkLoadFailed(false);
      return undefined;
    }

    let disposed = false;

    (async () => {
      const ready = await loadGoogleIdentityScript();
      if (!ready || disposed || !googleButtonHostRef.current) {
        if (!disposed) {
          setGoogleSdkLoadFailed(true);
        }
        return;
      }
      setGoogleSdkLoadFailed(false);

      const googleId = window.google?.accounts?.id;
      if (!googleId) {
        if (!disposed) {
          setGoogleSdkLoadFailed(true);
        }
        return;
      }

      try {
        googleId.initialize({
          client_id: googleAuthClientId,
          callback: (response) => {
            if (disposed) {
              return;
            }
            submitGoogleCredential(response?.credential, registerMode ? "register" : "login");
          }
        });

        googleButtonHostRef.current.innerHTML = "";
        googleId.renderButton(googleButtonHostRef.current, {
          theme: "outline",
          size: "large",
          shape: "pill",
          logo_alignment: "left",
          text: registerMode ? "signup_with" : "continue_with",
          width: 280
        });

        window.setTimeout(() => {
          if (disposed || !googleButtonHostRef.current) {
            return;
          }
          const hasRenderedButton = googleButtonHostRef.current.childElementCount > 0;
          if (!hasRenderedButton) {
            setGoogleSdkLoadFailed(true);
          }
        }, 120);
      } catch (_err) {
        if (!disposed) {
          setGoogleSdkLoadFailed(true);
        }
      }
    })();

    return () => {
      disposed = true;
      if (googleButtonHostRef.current) {
        googleButtonHostRef.current.innerHTML = "";
      }
    };
  }, [googleAuthClientId, registerMode, shouldLoadGoogleAuth, submitGoogleCredential]);

  async function sendEmailCode() {
    setError("");
    setNotice("");

    if (registerMode && !registerPrerequisitesMet) {
      setError(t("authLogin.registerPrerequisitesError"));
      return;
    }

    if (emailCodeCountdown > 0) {
      return;
    }

    if (!emailValidation.ok) {
      setError(emailValidation.message);
      return;
    }

    if (!captchaId || !String(captchaCode || "").trim()) {
      setError(
        forgotPasswordMode
          ? t("authLogin.enterCaptchaBeforeResetCode")
          : t("authLogin.enterCaptchaBeforeEmailCode")
      );
      return;
    }

    setSendingEmailCode(true);
    try {
      const emailPurpose = forgotPasswordMode ? "reset" : "register";
      const response = await fetch(`${apiBaseUrl}/auth/email-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: String(email || "").trim(),
          purpose: emailPurpose,
          captchaId,
          captchaCode: String(captchaCode || "").trim()
        })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || t("authLogin.sendEmailCodeFailed"));
      }

      setEmailCodeCountdown(Number(data.cooldownSeconds || 60));
      const hintText = String(data.hint || "").trim();
      const statusText = [
        translateAuthText(data.message || t("authLogin.emailCodeSent")),
        translateAuthText(hintText)
      ]
        .filter(Boolean)
        .join(" ");

      if (registerMode) {
        setVerifiedCaptchaIdForRegister(String(captchaId || "").trim());
        setVerifiedCaptchaCodeForRegister(String(captchaCode || "").trim());
      }

      setNotice(statusText);
    } catch (err) {
      setError(translateAuthText(String(err?.message || t("authLogin.sendEmailCodeFailed")).trim()));
    } finally {
      setSendingEmailCode(false);
      if (forgotPasswordMode) {
        loadCaptcha();
      }
    }
  }

  async function submitLogin(event) {
    event.preventDefault();
    setError("");
    setNotice("");
    setLoading(true);

    try {
      if (googleRegistrationMode) {
        if (password.length < 6) {
          throw new Error(t("authLogin.passwordMin"));
        }

        if (!confirmPassword) {
          throw new Error(t("authLogin.confirmPasswordRequired"));
        }

        if (password !== confirmPassword) {
          throw new Error(t("authLogin.passwordMismatch"));
        }

        const response = await fetch(`${apiBaseUrl}/auth/google/register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            credential: pendingGoogleRegistration.credential,
            password,
            confirmPassword,
            name: String(name || pendingGoogleRegistration.name || "").trim(),
            email: String(pendingGoogleRegistration.email || "").trim()
          })
        });
        const data = await parseJsonResponse(response);
        if (!response.ok) {
          throw new Error(translateAuthText(data.message || t("authLogin.googleAuthFailed")));
        }

        setPendingGoogleRegistration(null);
        await completeAuthenticatedSession(data);
        return;
      }

      if (registerMode) {
        const registerError = getRegisterFormError({
          username,
          email,
          password,
          confirmPassword,
          emailCode,
          usernameExists: usernameAvailability.exists,
          emailExists: emailAvailability.exists
        });
        if (registerError) {
          throw new Error(registerError);
        }

        const registerEmail = String(email || "").trim();
        const registerUsername = String(username || "").trim();
        const registerName = String(name || "").trim() || registerUsername || registerEmail.split("@")[0] || "New User";

        const registerResponse = await fetch(`${apiBaseUrl}/auth/register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username: registerUsername,
            name: registerName,
            email: registerEmail,
            password,
            confirmPassword,
            emailCode: String(emailCode || "").trim(),
            captchaId: String(verifiedCaptchaIdForRegister || "").trim(),
            captchaCode: String(verifiedCaptchaCodeForRegister || "").trim()
          })
        });

        const registerData = await registerResponse.json();
        if (!registerResponse.ok) {
          throw new Error(translateAuthText(registerData.message || t("authLogin.registerFailed")));
        }

        const registerRedirectMs = 4000;
        const registerSuccessMessage = t("authLogin.registerSuccessNotice");
        setAuthSuccessNotice(registerSuccessMessage);
        setAuthRedirectMs(registerRedirectMs);
        setPassword("");
        setConfirmPassword("");
        setEmailCode("");
        setEmailCodeCountdown(0);
        setVerifiedCaptchaIdForRegister("");
        setVerifiedCaptchaCodeForRegister("");
        setCaptchaCode("");
        localStorage.removeItem("ft_user_token");
        localStorage.removeItem("ft_user_profile");
        loadCaptcha();
        runDelayedAction(() => {
          switchToLoginMode();
          navigate("/login");
        }, registerRedirectMs);
        return;
      }

      if (forgotPasswordMode) {
        const resetError = getResetPasswordFormError({
          email,
          password,
          confirmPassword,
          emailCode,
          captchaId,
          captchaCode
        });
        if (resetError) {
          throw new Error(resetError);
        }

        const resetResponse = await fetch(`${apiBaseUrl}/auth/password-reset`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: String(email || "").trim(),
            newPassword: password,
            confirmPassword,
            emailCode: String(emailCode || "").trim(),
            captchaId,
            captchaCode: String(captchaCode || "").trim()
          })
        });

        const resetData = await resetResponse.json();
        if (!resetResponse.ok) {
          throw new Error(translateAuthText(resetData.message || t("authLogin.resetFailed")));
        }

        const resetSuccessMessage = t("authLogin.resetSuccessNotice");
        setAuthSuccessNotice(resetSuccessMessage);
        setAuthRedirectMs(1500);
        setNotice(translateAuthText(resetData.message || resetSuccessMessage));
        loadCaptcha();
        runDelayedAction(() => {
          switchToLoginMode();
          navigate("/login");
        });
        return;
      }

      if (!disableFrontendLoginCaptcha && (!captchaId || !captchaCode.trim())) {
        throw new Error(t("authLogin.captchaRequired"));
      }

      const response = await fetch(`${apiBaseUrl}/auth/login`, {
        method: "POST",
        cache: "no-store",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, captchaId, captchaCode })
      });
      const data = await parseJsonResponse(response);

      if (!response.ok) {
        const authError = new Error(data.message || t("authLogin.loginFailed"));
        authError.code = String(data?.code || "").trim();
        throw authError;
      }
      await completeAuthenticatedSession(data);
    } catch (err) {
      const message = String(err?.message || "").trim();
      const code = String(err?.code || "").trim();
      if (!message || /fetch failed|failed to fetch|networkerror/i.test(message)) {
        setError(t("authLogin.loginServiceUnavailable"));
      } else {
        setError(translateAuthText(code || message));
      }
      loadCaptcha();
    } finally {
      setLoading(false);
    }
  }

  function toggleRegisterMode() {
    if (!allowRegistration) {
      setError(registrationDisabledMessage);
      setNotice("");
      if (typeof window !== "undefined" && typeof window.alert === "function") {
        window.alert(registrationDisabledMessage);
      }
      return;
    }

    switchAuthView(registerMode ? "login" : "register");
  }

  function toggleForgotPasswordMode() {
    switchAuthView(forgotPasswordMode ? "login" : "forgot");
  }

  return (
    <StorefrontLayout
      apiBaseUrl={apiBaseUrl}
      currency={currency}
      onChangeCurrency={setCurrency}
    >
      <section className="login-page-shell" ref={authShellRef}>
        <div className="login-card">
          <aside className="login-brand-panel">
            <p className="login-kicker">LOYA SPORT</p>
            <h2>{t("authLogin.welcomeBack")}</h2>
            <p>
              {t("authLogin.brandIntro")}
            </p>
            <ul>
              <li>{t("authLogin.brandBullet1")}</li>
              <li>{t("authLogin.brandBullet2")}</li>
              <li>{t("authLogin.brandBullet3")}</li>
            </ul>
          </aside>

          <div className="login-form-panel">
            <h3>{authTitle}</h3>
            <p>{authDescription}</p>

            {recoveryMode && authSuccessNotice ? (
              <div className="login-success-panel" role="status" aria-live="polite" ref={authSuccessPanelRef}>
                <div className="login-success-icon" aria-hidden="true">✓</div>
                <div className="login-success-content">
                  <strong>{authSuccessNotice}</strong>
                  <p>{t("authLogin.redirectHintPrefix")} {Math.max(0, (authRedirectMs / 1000)).toFixed(1)} {t("authLogin.seconds")}{t("authLogin.redirectHintSuffix")}</p>
                </div>
                <button type="button" className="button login-success-button" onClick={switchToLoginMode}>
                  {t("authLogin.loginNow")}
                </button>
              </div>
            ) : (
            <form className="login-form" onSubmit={submitLogin} autoComplete={registerMode ? "off" : "on"}>

              {registerMode ? (
                <>
                  {/* 用户名 */}
                  <label>
                    <span>{t("authLogin.username")}</span>
                    <input
                      type="text"
                      name="register-username"
                      autoComplete="off"
                      value={username}
                      onChange={(event) => setUsername(event.target.value)}
                      placeholder={t("authLogin.usernamePlaceholder")}
                      required
                    />
                  </label>
                  <p className={`login-field-hint ${username && !usernameValidation.ok ? "login-field-hint-error" : usernameAvailability.exists || usernameAvailability.message.includes(t("authLogin.usernameVerifyFailed")) ? "login-field-hint-error" : usernameAvailability.message ? "login-field-hint-success" : ""}`}>
                    {!username
                      ? t("authLogin.usernameHelp")
                      : !usernameValidation.ok
                        ? usernameValidation.message
                        : usernameAvailability.exists
                          ? t("authLogin.usernameExistsHint")
                          : usernameAvailability.message || ""}
                  </p>
                  {usernameAvailability.exists ? (
                    <button type="button" className="login-switch-button" onClick={() => switchToLoginMode({ identifier: String(username || "").trim() })}>
                      {t("authLogin.usernameExistsAction")}
                    </button>
                  ) : null}

                  {/* 密码 */}
                  <label>
                    <span>{t("authLogin.password")}</span>
                    <input
                      type="password"
                      name="register-password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder={t("authLogin.passwordPlaceholder")}
                      required
                    />
                  </label>

                  {/* 确认密码 */}
                  <label>
                    <span>{t("authLogin.confirmPassword")}</span>
                    <input
                      type="password"
                      name="register-confirm-password"
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      placeholder={t("authLogin.confirmPasswordPlaceholder")}
                      required
                    />
                  </label>
                  {confirmPassword ? (
                    <p className={`login-field-hint ${password === confirmPassword ? "login-field-hint-success" : "login-field-hint-error"}`}>
                      {confirmPasswordState}
                    </p>
                  ) : null}

                  {/* 昵称（可选） */}
                  <label>
                    <span>{t("authLogin.nickname")}</span>
                    <input
                      type="text"
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      placeholder={t("authLogin.nicknamePlaceholder")}
                    />
                  </label>

                  {/* 邮箱 */}
                  <label>
                    <span>{t("authLogin.email")}</span>
                    <input
                      type="email"
                      name="register-email"
                      autoComplete="off"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder={t("authLogin.emailPlaceholder")}
                      required
                    />
                  </label>
                  <p className={`login-field-hint ${email && !emailValidation.ok ? "login-field-hint-error" : emailAvailability.exists || emailAvailability.message.includes(t("authLogin.emailVerifyFailed")) ? "login-field-hint-error" : emailAvailability.message ? "login-field-hint-success" : emailValidation.ok && email ? "login-field-hint-success" : ""}`}>
                    {!email
                      ? t("authLogin.emailCodeTargetHint")
                      : !emailValidation.ok
                        ? emailValidation.message
                        : emailAvailability.exists
                          ? t("authLogin.emailExistsHint")
                          : emailAvailability.message || `${emailValidation.message}${emailValidation.hint ? ` ${emailValidation.hint}` : ""}`}
                  </p>
                  {emailAvailability.exists ? (
                    <button type="button" className="login-switch-button" onClick={() => switchToLoginMode({ identifier: String(email || "").trim() })}>
                      {t("authLogin.emailExistsAction")}
                    </button>
                  ) : null}
                </>
              ) : null}

              {/* 只在非注册模式下渲染 recoveryMode 的邮箱输入框 */}
              {!registerMode && (
                <>
                  <label>
                    <span>{googleRegistrationMode || recoveryMode ? t("authLogin.email") : t("authLogin.usernameOrEmail")}</span>
                    <input
                      type={googleRegistrationMode || recoveryMode ? "email" : "text"}
                      name={googleRegistrationMode ? "google-email" : recoveryMode ? "recovery-email" : "login-identifier"}
                      autoComplete={googleRegistrationMode || recoveryMode ? "email" : "username"}
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder={googleRegistrationMode || recoveryMode ? t("authLogin.emailPlaceholder") : t("authLogin.usernameOrEmailPlaceholder")}
                      readOnly={googleRegistrationMode}
                      required
                    />
                  </label>
                  {googleRegistrationMode ? (
                    <p className="login-field-hint login-field-hint-success">
                      {i18nText("authLogin.googleLinkedEmailHint", "This verified Google email will be used to create your customer account.")}
                    </p>
                  ) : recoveryMode ? (
                    <p className={`login-field-hint ${email && !emailValidation.ok ? "login-field-hint-error" : emailValidation.ok && email ? "login-field-hint-success" : ""}`}>
                      {email
                        ? `${emailValidation.message}${emailValidation.hint ? ` ${emailValidation.hint}` : ""}`
                        : forgotPasswordMode
                          ? t("authLogin.forgotEmailHint")
                          : t("authLogin.emailCodeTargetHint")}
                    </p>
                  ) : null}
                </>
              )}

              {/* 只在非注册模式下渲染 recoveryMode 的密码和确认密码输入框 */}
              {!registerMode && (
                <>
                  <label>
                    <span>{t("authLogin.password")}</span>
                    <input
                      type="password"
                      name={recoveryMode ? "reset-new-password" : "login-password"}
                      autoComplete={recoveryMode ? "new-password" : "current-password"}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder={t("authLogin.passwordPlaceholder")}
                      required
                    />
                  </label>
                  {recoveryMode ? (
                    <label>
                      <span>{forgotPasswordMode ? t("authLogin.confirmNewPassword") : t("authLogin.confirmPassword")}</span>
                      <input
                        type="password"
                        name={forgotPasswordMode ? "reset-confirm-password" : "confirm-password"}
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                        placeholder={forgotPasswordMode ? t("authLogin.confirmNewPasswordPlaceholder") : t("authLogin.confirmPasswordPlaceholder")}
                        required={recoveryMode}
                      />
                    </label>
                  ) : null}
                  {recoveryMode && confirmPassword ? (
                    <p className={`login-field-hint ${password === confirmPassword ? "login-field-hint-success" : "login-field-hint-error"}`}>
                      {confirmPasswordState}
                    </p>
                  ) : null}
                </>
              )}

              {registerCaptchaStepVisible && shouldShowLoginCaptcha && !googleRegistrationMode ? (
                <div className="login-submit-row">
                  <div className="login-captcha-wrap">
                    <input
                      type="text"
                      value={captchaCode}
                      onChange={(event) => setCaptchaCode(event.target.value.toUpperCase())}
                      placeholder={recoveryMode ? (forgotPasswordMode ? t("authLogin.resetCaptcha") : t("authLogin.registerCaptcha")) : t("authLogin.captcha")}
                      maxLength={6}
                      required={shouldShowLoginCaptcha && (forgotPasswordMode || !recoveryMode)}
                    />
                    <button type="button" className="login-captcha-image" onClick={loadCaptcha} title={t("authLogin.refreshCaptcha")}>
                      {captchaImage ? (
                        <img src={captchaImage} alt={t("authLogin.captcha")} />
                      ) : (
                        <span>{t("authLogin.load")}</span>
                      )}
                    </button>
                  </div>
                </div>
              ) : null}

              {!recoveryMode ? (
                <button
                  className={`button login-submit ${shouldShowLoginCaptcha ? "" : "login-submit-full"}`.trim()}
                  type="submit"
                  disabled={loading}
                >
                  {loading ? t("authLogin.signingIn") : t("authLogin.login")}
                </button>
              ) : null}

              {recoveryMode && registerCaptchaStepVisible && shouldShowLoginCaptcha && !googleRegistrationMode ? (
                <p className="login-field-hint">
                  {forgotPasswordMode
                    ? t("authLogin.forgotCaptchaHint")
                    : t("authLogin.registerCaptchaHint")}
                </p>
              ) : null}

              {registerMode && !registerCaptchaStepVisible ? (
                <p className="login-field-hint">
                  {t("authLogin.registerStepHint")}
                </p>
              ) : null}

              {recoveryMode && registerCaptchaStepVisible && !googleRegistrationMode ? (
                <>
                  <div className="login-inline-row">
                    <label className="login-inline-field">
                      <span>{forgotPasswordMode ? t("authLogin.resetCode") : t("authLogin.emailCode")}</span>
                      <input
                        type="text"
                        value={emailCode}
                        onChange={(event) => setEmailCode(event.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                        placeholder={forgotPasswordMode ? t("authLogin.resetCodePlaceholder") : t("authLogin.emailCodePlaceholder")}
                        maxLength={6}
                        required={recoveryMode}
                      />
                    </label>
                    <button
                      type="button"
                      className="login-inline-button"
                      onClick={sendEmailCode}
                      disabled={!canSendEmailCode}
                    >
                      {sendingEmailCode
                        ? t("authLogin.sending")
                        : emailCodeCountdown > 0
                          ? `${t("authLogin.resendIn")} ${emailCodeCountdown}s`
                          : forgotPasswordMode
                            ? t("authLogin.sendResetCode")
                            : t("authLogin.sendEmailCode")}
                    </button>
                  </div>
                </>
              ) : null}

              {recoveryMode ? (
                <button className="button login-submit login-submit-full" type="submit" disabled={loading}>
                  {loading ? (forgotPasswordMode ? t("authLogin.updating") : t("authLogin.creating")) : forgotPasswordMode ? t("authLogin.resetPassword") : t("authLogin.createAccount")}
                </button>
              ) : null}

              {!recoveryMode ? null : (
                <p className="login-field-hint">
                  {googleRegistrationMode
                    ? i18nText("authLogin.googleCreatePasswordHint", "Set and confirm a password. Your Google-verified email will be used as the new customer account.")
                    : forgotPasswordMode
                    ? t("authLogin.forgotRequirementHint")
                    : t("authLogin.registerRequirementHint")}
                </p>
              )}

              {shouldShowGoogleAuth ? (
                <div className="login-social-auth">
                  <p className="login-social-divider">
                    <span>{t("authLogin.orContinueWith")}</span>
                  </p>
                  {!googleSdkRequested ? (
                    <button
                      type="button"
                      className="login-google-fallback-button"
                      onClick={requestGoogleAuthWidget}
                    >
                      <span className="login-google-fallback-icon" aria-hidden="true">
                        <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" focusable="false">
                          <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.654 32.657 29.239 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.06 0 5.842 1.154 7.959 3.041l5.657-5.657C34.046 6.053 29.27 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                          <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.06 0 5.842 1.154 7.959 3.041l5.657-5.657C34.046 6.053 29.27 4 24 4c-7.682 0-14.348 4.337-17.694 10.691z"/>
                          <path fill="#4CAF50" d="M24 44c5.165 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.144 35.091 26.689 36 24 36c-5.219 0-9.623-3.317-11.283-7.946l-6.52 5.025C9.51 39.556 16.227 44 24 44z"/>
                          <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.084 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
                        </svg>
                      </span>
                      <span>{i18nText("authLogin.googleLoadAction", "Continue with Google")}</span>
                    </button>
                  ) : (
                    <div className="login-google-button" ref={googleButtonHostRef} />
                  )}
                  {googleSdkLoadFailed ? (
                    <button
                      type="button"
                      className="login-google-fallback-button"
                      onClick={handleGoogleFallbackClick}
                    >
                      <span className="login-google-fallback-icon" aria-hidden="true">
                        <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" focusable="false">
                          <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.654 32.657 29.239 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.06 0 5.842 1.154 7.959 3.041l5.657-5.657C34.046 6.053 29.27 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                          <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.06 0 5.842 1.154 7.959 3.041l5.657-5.657C34.046 6.053 29.27 4 24 4c-7.682 0-14.348 4.337-17.694 10.691z"/>
                          <path fill="#4CAF50" d="M24 44c5.165 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.144 35.091 26.689 36 24 36c-5.219 0-9.623-3.317-11.283-7.946l-6.52 5.025C9.51 39.556 16.227 44 24 44z"/>
                          <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.084 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
                        </svg>
                      </span>
                      <span>{t("authLogin.googleFallbackAction")}</span>
                    </button>
                  ) : null}
                  {!googleSdkRequested ? (
                    <p className="login-field-hint">
                      {i18nText(
                        "authLogin.googleLoadHint",
                        "Google sign-in will load only when you choose to continue with Google."
                      )}
                    </p>
                  ) : null}
                  {googleLoading ? (
                    <p className="login-field-hint">{t("authLogin.googleAuthorizing")}</p>
                  ) : null}
                  {googleSdkLoadFailed ? (
                    <p className="login-field-hint login-field-hint-error">
                      {storefrontLocale === "zh"
                        ? "Google 登录组件渲染失败，请检查网络、浏览器控制台报错，并确认 Google OAuth 的 Authorized JavaScript origins 已包含当前站点地址。"
                        : "Google sign-in widget failed to render. Check network, browser console errors, and ensure the current site origin is listed in Google OAuth Authorized JavaScript origins."}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {!registerMode && shouldShowLoginCaptcha ? (
                <p className="login-field-hint">{t("authLogin.captchaRefreshHint")}</p>
              ) : null}

              <div className="login-switch-actions">
                <button type="button" className="login-switch-button" onClick={toggleRegisterMode}>
                  {registerMode ? t("authLogin.switchToLogin") : t("authLogin.switchToRegister")}
                </button>
                {!registerMode && !googleRegistrationMode ? (
                  <button type="button" className="login-switch-button" onClick={toggleForgotPasswordMode}>
                    {forgotPasswordMode ? t("authLogin.backToLogin") : t("authLogin.forgotPassword")}
                  </button>
                ) : null}
              </div>
            </form>
            )}

            {notice && !authSuccessNotice ? (
              <div className="state login-feedback login-feedback-success" role="status" aria-live="polite">
                <span className="login-feedback-icon" aria-hidden="true">✓</span>
                <span>{notice}</span>
              </div>
            ) : null}
            {error ? (
              <div className="state login-feedback login-feedback-error" role="alert">
                <span className="login-feedback-icon" aria-hidden="true">!</span>
                <span>{error}</span>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </StorefrontLayout>
  );
}
